import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { waLink } from "@/config";

const links = [
  { to: "/inventory", label: "Inventory" },
  { to: "/sell", label: "Sell Your Bike" },
  { to: "/#exchange", label: "Exchange", hash: true },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHash = (e, to) => {
    e.preventDefault();
    setOpen(false);
    const [path, hash] = to.split("#");
    if (path === "/" && window.location.pathname !== "/") {
      navigate(`/#${hash}`);
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 400);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link" aria-label="Deal 2 Wheel home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-links">
          {links.map((l) =>
            l.hash ? (
              <a
                key={l.label}
                href={l.to}
                onClick={(e) => goHash(e, l.to)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-white/70 hover:text-volt transition-colors duration-300 uppercase tracking-widest"
              >
                {l.label}
              </a>
            ) : (
              <NavLink
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium uppercase tracking-widest transition-colors duration-300 ${
                    isActive ? "text-volt" : "text-white/70 hover:text-volt"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink("Hi Deal 2 Wheel! I want to know more about your bikes.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-volt text-black font-semibold text-sm px-5 py-2.5 rounded-full hover:scale-105 volt-glow transition-transform duration-300"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp Us
          </a>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden backdrop-blur-xl bg-black/90 border-t border-white/10 px-5 py-6 flex flex-col gap-5"
            data-testid="nav-mobile-menu"
          >
            {links.map((l) =>
              l.hash ? (
                <a
                  key={l.label}
                  href={l.to}
                  onClick={(e) => goHash(e, l.to)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-xl uppercase tracking-tight text-white"
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-xl uppercase tracking-tight text-white"
                >
                  {l.label}
                </NavLink>
              )
            )}
            <a
              href={waLink("Hi Deal 2 Wheel! I want to know more about your bikes.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 bg-volt text-black font-semibold px-5 py-3 rounded-full"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Us
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
